import { useState, useEffect } from 'react';
import axios from 'axios';

import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Divider, 
  InputAdornment,
  OutlinedInput,
  ListItemText,
  Checkbox,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  FormControlLabel,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { styled } from '@mui/material/styles';

// API base URL - adjust as needed for your environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Styled component for file upload
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Role types from backend
const roleTypes = [
  "CEO", 
  "CTO", 
  "Board of directors", 
  "Section manager/Department manager", 
  "Manager", "Project Manager", "Product Manager", "Product Owner", "Finance Manager", "HR", "HR Manager", 
  "Hiring Manager", 
  "Principal engineer/Architect", 
  "Team Lead", 
  "Senior Software Engineer", 
  "Software Engineer", 
  "Junior Software Engineer", 
  "Intern"
];

export default function EmployeeForm() {
  // State for available projects (will be fetched from API)
  const [availableProjects, setAvailableProjects] = useState([]);
  
  // State for available companies/contracts (will be fetched from API)
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [companyCountry, setCompanyCountry] = useState('India'); // Default to India
  const [nationalIdLabel, setNationalIdLabel] = useState('PAN / Aadhaar');
  const [currencySymbol, setCurrencySymbol] = useState('₹');
  const [bankCodeLabel, setBankCodeLabel] = useState('IFSC Code');
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: 'info',
    message: ''
  });

  // Selected company state (user selects from dropdown)
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Tab State: 'direct' or 'vendor'
  const [activeTab, setActiveTab] = useState('direct');

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    if (newValue === 'direct') {
      const directOption = availableCompanies.find(c => c.id === 'direct') || {
        id: 'direct',
        companyName: 'Direct Contractor (No Vendor Agency)',
        companyAddress: 'N/A',
        status: 'active',
        contractStartDate: new Date().toISOString(),
        contractEndDate: new Date().toISOString()
      };
      setSelectedCompany(directOption);
    } else {
      setSelectedCompany(null);
    }

    // Reset employee project selections when company changes
    const updatedEmployees = employees.map(employee => ({
      ...employee,
      projectIds: [],
      customProjectIds: []
    }));
    setEmployees(updatedEmployees);
  };
  
  // Array of employees
  const [employees, setEmployees] = useState([
    createEmptyEmployeeData()
  ]);


  // Validation functions
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhoneNumber = (phone) => {
  const phoneRegex = /^[6-9]\d{9}$/; // Indian mobile number format
  return phoneRegex.test(phone.replace(/\s+/g, ''));
};

const validateAadharNumber = (aadhar) => {
  if (companyCountry.toLowerCase() !== 'india') return aadhar && aadhar.trim().length > 0;
  const aadharRegex = /^\d{12}$/;
  return aadharRegex.test(aadhar.replace(/\s+/g, ''));
};

const validatePanNumber = (pan) => {
  if (companyCountry.toLowerCase() !== 'india') return pan && pan.trim().length > 0;
  const panRegex = /^[A-Z]{5}\d{4}[A-Z]{1}$/;
  return panRegex.test(pan.toUpperCase());
};

  // Function to create an empty employee data object
  function createEmptyEmployeeData() {
    return {
      fullName: '',
      email: '',
      companyEmail: '',
      phoneNumber: '',
      aadhaarNumber: '',
      gender: '',
      panNumber: '',
      designation: '',
      department: '',
      roleType: '',
      customRoleType: '',
      reportingManager: '',
      skills: '',
      skillsDescription: '',
      projectIds: [], // Regular project IDs
      customProjectIds: [], // Custom project IDs
      hourlyRate: '',
      approvedHours: '160',
      contractPeriod: '',
      contractStartDate: '',
      contractEndDate: '',
      address: '',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      accountName: '',
      costCenterId: '',
      // Track files separately for display in the UI
      files: {
        aadharCard: null,
        panCard: null,
        ndaDoc: null
      }
    };
  }

  const [costCenters, setCostCenters] = useState([]);

  // Fetch projects and companies on component mount
  useEffect(() => {
    fetchCompanyData();
    fetchProjects();
    fetchCompanies();
    fetchCostCenters();
  }, []);

  const [companyInfo, setCompanyInfo] = useState({ emailGenMode: 'automated', emailDomain: '', registration_no: null });

  const getEmailSuggestions = (fullName) => {
    if (companyInfo.emailGenMode !== 'automated' || !companyInfo.emailDomain || !fullName?.trim()) {
      return [];
    }
    const clean = fullName.trim().toLowerCase().replace(/[^a-zA-Z\s]/g, '');
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return [];
    const f = parts[0];
    const l = parts.length > 1 ? parts[parts.length - 1] : '';
    const domain = companyInfo.emailDomain.toLowerCase();
    if (l) {
      return [`${f}.${l}@${domain}`, `${f}${l}@${domain}`];
    }
    return [`${f}@${domain}`];
  };

  const checkCompanyEmailUniqueness = async (index, emailVal, personalVal) => {
    const errorKey = `${index}-companyEmail`;
    const trimmed = (emailVal || '').trim().toLowerCase();
    const newErrors = { ...validationErrors };

    if (!trimmed) {
      if (companyInfo.emailGenMode === 'manual') {
        newErrors[errorKey] = 'Company email is required when Email Generation mode is set to Manual.';
      } else {
        delete newErrors[errorKey];
      }
      setValidationErrors(newErrors);
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) {
      newErrors[errorKey] = 'Invalid company email format (e.g., user@example.com).';
      setValidationErrors(newErrors);
      return;
    }

    const domain = (companyInfo.emailDomain || '').toLowerCase();
    if (domain && !trimmed.endsWith(`@${domain}`)) {
      newErrors[errorKey] = `Company email must end with the registered domain: @${domain}`;
      setValidationErrors(newErrors);
      return;
    }

    if (personalVal && trimmed === personalVal.trim().toLowerCase()) {
      newErrors[errorKey] = 'Company email cannot be identical to personal email.';
      setValidationErrors(newErrors);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const targetCmpId = companyInfo.registration_no;
      if (targetCmpId) {
        const res = await axios.get(`${API_BASE_URL}/api/admin/employees/check-email?email=${encodeURIComponent(trimmed)}&company_id=${targetCmpId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.exists) {
          newErrors[errorKey] = 'This company email is already registered in this company.';
          setValidationErrors(newErrors);
          return;
        }
      }
      delete newErrors[errorKey];
      setValidationErrors(newErrors);
    } catch {
      delete newErrors[errorKey];
      setValidationErrors(newErrors);
    }
  };

  const checkPersonalEmailUniqueness = async (index, emailVal, companyVal) => {
    const errorKey = `${index}-email`;
    const trimmed = (emailVal || '').trim().toLowerCase();
    const newErrors = { ...validationErrors };

    if (!trimmed) {
      newErrors[errorKey] = 'Personal email is required.';
      setValidationErrors(newErrors);
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) {
      newErrors[errorKey] = 'Please enter a valid personal email address.';
      setValidationErrors(newErrors);
      return;
    }

    if (companyVal && trimmed === companyVal.trim().toLowerCase()) {
      newErrors[errorKey] = 'Personal email cannot be identical to company email.';
      setValidationErrors(newErrors);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const targetCmpId = companyInfo.registration_no;
      if (targetCmpId) {
        const res = await axios.get(`${API_BASE_URL}/api/admin/employees/check-personal-email?email=${encodeURIComponent(trimmed)}&company_id=${targetCmpId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.exists) {
          newErrors[errorKey] = 'This personal email is already registered in this company.';
          setValidationErrors(newErrors);
          return;
        }
      }
      delete newErrors[errorKey];
      setValidationErrors(newErrors);
    } catch {
      delete newErrors[errorKey];
      setValidationErrors(newErrors);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.company_id) {
        const { data } = await axios.get(`${API_BASE_URL}/api/cmp/${payload.company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (data.success && data.data) {
          setCompanyInfo({
            emailGenMode: data.data.emailGenMode || 'automated',
            emailDomain: data.data.emailDomain || '',
            registration_no: data.data.registration_no || payload.company_id
          });
          if (data.data.country) {
            setCompanyCountry(data.data.country);
            if (data.data.nationalIdLabel) setNationalIdLabel(data.data.nationalIdLabel);
            if (data.data.currency_symbol) setCurrencySymbol(data.data.currency_symbol);
            if (data.data.bankCodeLabel) setBankCodeLabel(data.data.bankCodeLabel);
          }
        }
      }
    } catch (error) {
      // console.error("Error fetching company country:", error);
    }
  };

  const fetchCostCenters = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/cost-centers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCostCenters(response.data.data || []);
      }
    } catch (error) {
      //console.error("Error fetching cost centers:", error);
    }
  };

  // Function to fetch projects from API
  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/addteam/project/manager-active`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      );
      if (response.data) {
        setAvailableProjects(response.data);
      }
    } catch (error) {
      //console.error('Error fetching projects:', error);
      setAlert({
        open: true,
        severity: 'error',
        message: 'Failed to load projects. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch companies/contracts from API
  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/contract/companies`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
         
          }
        }
      );
      if (response.data) {
        const directOption = {
          id: 'direct',
          companyName: 'Direct Contractor (No Vendor Agency)',
          companyAddress: 'N/A',
          status: 'active',
          contractStartDate: new Date().toISOString(),
          contractEndDate: new Date().toISOString()
        };
        setAvailableCompanies([directOption, ...response.data]);
        setSelectedCompany(directOption); // Auto-fill direct option on mount
      }
    } catch (error) {
      //console.error('Error fetching companies:', error);
      setAlert({
        open: true,
        severity: 'error',
        message: 'Failed to load companies. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle company selection from dropdown
  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    const company = availableCompanies.find(c => c.id === companyId);
    setSelectedCompany(company);
    
    // Reset employee project selections when company changes
    const updatedEmployees = employees.map(employee => ({
      ...employee,
      projectIds: [],
      customProjectIds: []
    }));
    
    setEmployees(updatedEmployees);
  };

  // Handle form field changes for a specific employee
  const handleEmployeeChange = (index, e) => {
    const { name, value, type, checked, files } = e.target;
    const updatedEmployees = [...employees];
    
    if (type === 'file') {
      updatedEmployees[index] = {
        ...updatedEmployees[index],
        files: {
          ...updatedEmployees[index].files,
          [name]: files[0]
        }
      };
    } else {
      let finalValue = type === 'checkbox' ? checked : value;
      if (name === 'phoneNumber') {
        finalValue = (value || '').replace(/\D/g, '').slice(0, 10);
      }
      updatedEmployees[index] = {
        ...updatedEmployees[index],
        [name]: finalValue
      };
  
      // Real-time validation
      const newValidationErrors = { ...validationErrors };
      const errorKey = `${index}-${name}`;
  
      switch (name) {
        case 'email':
          if (value && !validateEmail(value)) {
            newValidationErrors[errorKey] = 'Please enter a valid email address';
          } else if (employees[index]?.companyEmail && value.trim().toLowerCase() === employees[index].companyEmail.trim().toLowerCase()) {
            newValidationErrors[errorKey] = 'Personal email cannot be identical to company email.';
          } else {
            delete newValidationErrors[errorKey];
          }
          break;
        case 'companyEmail':
          checkCompanyEmailUniqueness(index, value, employees[index]?.email);
          break;
        case 'phoneNumber':
          if (value && !validatePhoneNumber(value)) {
            newValidationErrors[errorKey] = 'Please enter a valid 10-digit Indian mobile number';
          } else {
            delete newValidationErrors[errorKey];
          }
          break;
        case 'aadhaarNumber':
          if (value && !validateAadharNumber(value)) {
            newValidationErrors[errorKey] = companyCountry.toLowerCase() === 'india' ? 'Please enter a valid 12-digit Aadhar number' : 'Invalid format';
          } else {
            delete newValidationErrors[errorKey];
          }
          break;
        case 'panNumber':
          if (value && !validatePanNumber(value)) {
            newValidationErrors[errorKey] = companyCountry.toLowerCase() === 'india' ? 'Please enter a valid PAN number (e.g., ABCDE1234F)' : 'Invalid format';
          } else {
            delete newValidationErrors[errorKey];
          }
          break;
      }
  
      setValidationErrors(newValidationErrors);
    }
    
    setEmployees(updatedEmployees);
  };

  // Handle employee project selection change for both regular and custom projects
  const handleEmployeeProjectChange = (index, event) => {
    const { value } = event.target;
    const updatedEmployees = [...employees];
    
    // Create arrays to store regular and custom project IDs
    const regularProjectIds = [];
    const customProjectIds = [];
    
    // Separate regular and custom project IDs
    value.forEach(projectId => {
      // Check if it's a regular project
      if (typeof projectId === 'string' && projectId.startsWith('regular_')) {
        // Regular project - extract the numeric ID
        const regularId = Number(projectId.replace('regular_', ''));
        if (selectedCompany && 
            selectedCompany.selectedProjects && 
            selectedCompany.selectedProjects.includes(regularId)) {
          regularProjectIds.push(regularId);
        }
      } 
      // Check if it's a custom project
      else if (typeof projectId === 'string' && projectId.startsWith('custom_')) {
        // Extract custom project ID
        const customId = Number(projectId.replace('custom_', ''));
        if (selectedCompany && 
            selectedCompany.customProjectObjects && 
            selectedCompany.customProjectObjects.some(p => p.id === customId)) {
          customProjectIds.push(customId);
        }
      }
    });
    
    // Update employee data with separated project IDs
    updatedEmployees[index] = {
      ...updatedEmployees[index],
      projectIds: regularProjectIds,
      customProjectIds: customProjectIds
    };
    
    setEmployees(updatedEmployees);
  };

  // Add a new employee
// Update addEmployee function
const addEmployee = () => {
  setEmployees([...employees, createEmptyEmployeeData()]);
  // Clear any validation errors for the form
  const newValidationErrors = { ...validationErrors };
  Object.keys(newValidationErrors).forEach(key => {
    if (key.startsWith(`${employees.length}-`)) {
      delete newValidationErrors[key];
    }
  });
  setValidationErrors(newValidationErrors);
};

// Update removeEmployee function
const removeEmployee = (index) => {
  if (employees.length > 1) {
    const updatedEmployees = employees.filter((_, i) => i !== index);
    setEmployees(updatedEmployees);
    
    // Clear validation errors for the removed employee
    const newValidationErrors = { ...validationErrors };
    Object.keys(newValidationErrors).forEach(key => {
      if (key.startsWith(`${index}-`)) {
        delete newValidationErrors[key];
      }
    });
    setValidationErrors(newValidationErrors);
  }
};

  // Close alert handler
  const handleAlertClose = () => {
    setAlert({
      ...alert,
      open: false
    });
  };

  // Submit form data to the API
  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasValidationErrors = false;
    const newValidationErrors = {};
    
    for (let i = 0; i < employees.length; i++) {
      const employee = employees[i];
      
      // Email validation
      if (employee.email && !validateEmail(employee.email)) {
        newValidationErrors[`${i}-email`] = 'Please enter a valid email address';
        hasValidationErrors = true;
      }

      // Company Email validation
      if (companyInfo.emailGenMode === 'manual' && !employee.companyEmail) {
        newValidationErrors[`${i}-companyEmail`] = 'Company email is required when Email Generation mode is Manual.';
        hasValidationErrors = true;
      } else if (employee.companyEmail) {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(employee.companyEmail.trim())) {
          newValidationErrors[`${i}-companyEmail`] = 'Invalid company email format.';
          hasValidationErrors = true;
        } else if (companyInfo.emailDomain && !employee.companyEmail.trim().toLowerCase().endsWith(`@${companyInfo.emailDomain.toLowerCase()}`)) {
          newValidationErrors[`${i}-companyEmail`] = `Company email must end with @${companyInfo.emailDomain.toLowerCase()}`;
          hasValidationErrors = true;
        } else if (employee.email && employee.email.trim().toLowerCase() === employee.companyEmail.trim().toLowerCase()) {
          newValidationErrors[`${i}-companyEmail`] = 'Company email cannot be identical to personal email.';
          hasValidationErrors = true;
        }
      }
      
      // Phone validation
      if (employee.phoneNumber && !validatePhoneNumber(employee.phoneNumber)) {
        newValidationErrors[`${i}-phoneNumber`] = 'Please enter a valid 10-digit Indian mobile number';
        hasValidationErrors = true;
      }
      
      // Aadhar validation
      if (employee.aadhaarNumber && !validateAadharNumber(employee.aadhaarNumber)) {
        newValidationErrors[`${i}-aadhaarNumber`] = companyCountry.toLowerCase() === 'india' ? 'Please enter a valid 12-digit Aadhar number' : 'Invalid format';
        hasValidationErrors = true;
      }
      
      // PAN validation
      if (employee.panNumber && !validatePanNumber(employee.panNumber)) {
        newValidationErrors[`${i}-panNumber`] = companyCountry.toLowerCase() === 'india' ? 'Please enter a valid PAN number' : 'Invalid format';
        hasValidationErrors = true;
      }
    }
    
    if (hasValidationErrors) {
      setValidationErrors(newValidationErrors);
      setAlert({
        open: true,
        severity: 'error',
        message: 'Please fix the validation errors before submitting.'
      });
      return;
    }
    
    // Validate form
    if (!selectedCompany) {
      setAlert({
        open: true,
        severity: 'error',
        message: 'Please select a company first.'
      });
      return;
    }
    
    // Check if any required employee fields are missing
    for (let i = 0; i < employees.length; i++) {
      const employee = employees[i];
      
      const isDirect = selectedCompany && selectedCompany.id === 'direct';
      // Check if at least one project (regular or custom) is selected
      const hasProjects = employee.projectIds.length > 0 || employee.customProjectIds.length > 0;
      
      if (!employee.fullName || 
          !employee.email || 
          !employee.phoneNumber || 
          !employee.gender ||
          !employee.designation ||
          !employee.department || 
          !employee.roleType || 
          (employee.roleType === 'Other' && !employee.customRoleType) ||
          !employee.reportingManager ||
          (!isDirect && !hasProjects)) {
            
        setAlert({
          open: true,
          severity: 'error',
          message: `Please fill all required fields for Employee #${i + 1}.`
        });
        return;
      }

      // Check direct contractor specific fields if company is 'direct'
      if (selectedCompany && selectedCompany.id === 'direct') {
        if (!employee.hourlyRate || 
            !employee.contractPeriod || 
            !employee.contractStartDate || 
            !employee.contractEndDate || 
            !employee.address ||
            !employee.bankName || 
            !employee.accountNumber || 
            !employee.ifscCode || 
            !employee.accountName) {
          setAlert({
            open: true,
            severity: 'error',
            message: `Please fill all billing, contract period, dates, address, and bank details for Employee #${i + 1}.`
          });
          return;
        }
      }
    }
    
    try {
      const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(true);
      
      // Prepare form data for API call
      const formData = new FormData();
      
      // Add company ID
      formData.append('companyId', selectedCompany.id);
      
      // Serialize employee data properly without files property
      const employeesData = employees.map(employee => {
        const { files, customProjectIds, ...employeeData } = employee;
        
        // Ensure projectIds doesn't contain null values
        employeeData.projectIds = employeeData.projectIds.filter(id => 
          id !== null && id !== undefined
        );
        
        // Add custom project IDs to the structure
        employeeData.customProjectIds = customProjectIds.filter(id => 
          id !== null && id !== undefined
        );
        
        if (employeeData.roleType === 'Other') {
          employeeData.roleType = (employeeData.customRoleType || '').trim();
        }
        
        return employeeData;
      });
      
      // Add employees data with proper stringification
      formData.append('employees', JSON.stringify(employeesData));
      
      // Add files for each employee with proper index tracking
      employees.forEach((employee, index) => {
        if (employee.files.aadharCard) {
          formData.append(`aadharCard-${index}`, employee.files.aadharCard);
        }
        
        if (employee.files.panCard) {
          formData.append(`panCard-${index}`, employee.files.panCard);
        }
        
        if (employee.files.ndaDoc) {
          formData.append(`ndaDoc-${index}`, employee.files.ndaDoc);
        }
      });
      
      // Send data to API
      const response = await axios.post(`${API_BASE_URL}/api/empcontract/employee/bulk`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (response.data.success) {
        setAlert({
          open: true,
          severity: 'success',
          message: `Successfully added ${employees.length} employee(s).`
        });
        
        // Reset form after successful submission
        setEmployees([createEmptyEmployeeData()]);
        setSelectedCompany(null);
      } else {
        throw new Error(response.data.message || 'Failed to add employees');
      }
    } catch (error) {
      //console.error('Error submitting form:', error);
      // Log the actual response error if it exists
      if (error.response) {
        //console.error('Server response:', error.response.data);
      }
      setAlert({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || error.message || 'Failed to submit the form. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // If data is loading, show loading indicator
  if (loading && availableProjects.length === 0 && availableCompanies.length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Get available projects for the selected company
  const companyProjects = selectedCompany 
    ? (selectedCompany.id === 'direct' 
        ? availableProjects 
        : (selectedCompany.selectedProjects 
            ? availableProjects.filter(project => selectedCompany.selectedProjects.includes(project.project_id))
            : []))
    : [];
  
  // Get custom projects for the selected company
  const customProjects = selectedCompany && selectedCompany.customProjectObjects
    ? selectedCompany.customProjectObjects
    : [];

  // Create a combined array of projects with unique identifiers
  // This prevents the Fragment error in Select component  
  const combinedProjects = [
    ...companyProjects.map(project => ({
      id: `regular_${project.project_id}`,
      name: project.name,
      type: 'regular',
      originalId: project.project_id
    })),
    ...customProjects.map(project => ({
      id: `custom_${project.id}`,
      name: project.name,
      type: 'custom',
      originalId: project.id
    }))
  ];
    
  return (
    <Container maxWidth="1400px" sx={{ py: 4 }}>
      {/* Alert for success/error messages */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleAlertClose} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
      
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4"  fontWeight="bold" >
          Add Contract Employee
        </Typography>
      </Paper>

      {/* Contract Options Helper Banner */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f9', borderLeft: '6px solid #1976d2', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#1565c0', mb: 1 }}>
            💡 Important Guidance on Contract Types
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 1.5, lineHeight: 1.6 }}>
            Please select the appropriate contract arrangement from the <b>Select Company</b> dropdown below:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box p={1.5} sx={{ bgcolor: 'white', borderRadius: 1.5, height: '100%', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="bold" sx={{ color: '#1a237e', mb: 0.5 }}>
                  🏢 Vendor Contract (Agency)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
                  Select the vendor agency name (e.g., <i>Info Tech</i> or <i>TransportPlus</i>) from the dropdown. 
                  You should then assign the contractor to their designated active company project and fill out their professional profile.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box p={1.5} sx={{ bgcolor: 'white', borderRadius: 1.5, height: '100%', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="bold" sx={{ color: '#2e7d32', mb: 0.5 }}>
                  👤 Direct Contractor (Company Contract)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
                  Select <b>Direct Contractor (No Vendor Agency)</b> from the dropdown.
                  This is for direct professional service agreements where you will directly specify billing rates (hourly), contract periods, and bank remittance details.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 6 }}>
        {/* CONTRACT TYPE SELECTOR - MODERN PREMIUM TABS */}
        <Paper 
          elevation={0}
          variant="outlined"
          sx={{ 
            mb: 4, 
            borderColor: '#e2e8f0', 
            borderRadius: '16px', 
            overflow: 'hidden',
            bgcolor: '#f8fafc',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)'
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            
  scrollButtons="auto"

            sx={{
              minHeight: '70px',
              '& .MuiTabs-indicator': {
                height: '4px',
                borderRadius: '4px 4px 0 0',
                backgroundColor: '#1976d2',
              },
            }}
          >
            <Tab
              value="direct"
              icon={<PersonAddIcon sx={{ fontSize: '22px', color: activeTab === 'direct' ? '#1976d2' : '#64748b' }} />}
              iconPosition="start"
              label={
                <Box sx={{ textAlign: 'left', ml: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'none', color: activeTab === 'direct' ? '#1976d2' : '#475569', lineHeight: 1.2 }}>
                    Direct Contractor
                  </Typography>
                  <Typography variant="caption" sx={{ textTransform: 'none', color: '#64748b', display: { xs: 'none', sm: 'block' }, mt: 0.5 }}>
                    Individual consultant contract (direct billing & bank details)
                  </Typography>
                </Box>
              }
              sx={{
                py: 2,
                minHeight: '70px',
                transition: 'all 0.3s ease',
                borderBottom: '2px solid transparent',
                '&.Mui-selected': {
                  bgcolor: '#fff',
                },
                '&:hover:not(.Mui-selected):not(.Mui-disabled)': {
                  bgcolor: '#f1f5f9',
                },
              }}
            />
            <Tab
              value="vendor"
              icon={<CloudUploadIcon sx={{ fontSize: '22px', color: activeTab === 'vendor' ? '#1976d2' : '#64748b' }} />}
              iconPosition="start"
              label={
                <Box sx={{ textAlign: 'left', ml: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'none', color: activeTab === 'vendor' ? '#1976d2' : '#475569', lineHeight: 1.2 }}>
                    Vendor Agency Contractor
                  </Typography>
                  <Typography variant="caption" sx={{ textTransform: 'none', color: '#64748b', display: { xs: 'none', sm: 'block' }, mt: 0.5 }}>
                    Add employee under a registered third-party vendor agency
                  </Typography>
                </Box>
              }
              sx={{
                py: 2,
                minHeight: '70px',
                transition: 'all 0.3s ease',
                borderBottom: '2px solid transparent',
                '&.Mui-selected': {
                  bgcolor: '#fff',
                },
                '&:hover:not(.Mui-selected):not(.Mui-disabled)': {
                  bgcolor: '#f1f5f9',
                },
              }}
            />
          </Tabs>
        </Paper>

        {/* Company Selection Card - Only shown for Vendor Agency Contractor tab */}
        {activeTab === 'vendor' && (
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3, borderLeft: '6px solid #1976d2' }}>
            <Typography variant="h6" component="h2" fontWeight="bold" mb={1} color="primary">
              Select Vendor Agency Partner
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2.5}>
              Choose the registered vendor firm that manages and pays this contractor.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="company-select-label">Vendor Agency Company</InputLabel>
                  <Select
                    labelId="company-select-label"
                    id="company-select"
                    value={selectedCompany ? selectedCompany.id : ''}
                    onChange={handleCompanyChange}
                    label="Vendor Agency Company"
                  >
                    <MenuItem value="">
                      <em>-- Select a registered vendor/agency --</em>
                    </MenuItem>
                    {availableCompanies
                      .filter(company => company.id !== 'direct')
                      .map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.companyName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {selectedCompany && (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                  🏢 Vendor Firm Corporate Details:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Name:</strong> <span style={{ color: '#334155', fontWeight: 500 }}>{selectedCompany.companyName}</span>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      <strong>Address:</strong> <span style={{ color: '#334155', fontWeight: 500 }}>{selectedCompany.companyAddress}</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Contract Duration:</strong> <span style={{ color: '#334155', fontWeight: 500 }}>{new Date(selectedCompany.contractStartDate).toLocaleDateString()} - {new Date(selectedCompany.contractEndDate).toLocaleDateString()}</span>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      <strong>Status:</strong> <span style={{ color: '#334155', fontWeight: 500 }}>{selectedCompany.status?.toUpperCase()}</span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        )}

        {selectedCompany && (
          <>
            {/* Employee Forms - One per employee */}
            {employees.map((employee, index) => (
              <Card 
                key={index} 
                elevation={3} 
                sx={{ 
                  mb: 4, 
                  position: 'relative',
                  border: '2px solid',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  overflow: 'visible'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" component="h2" fontWeight="bold" color="primary.dark">
                      Employee
                    </Typography>
                    
                    {employees.length > 1 && (
                      <Button 
                        variant="text" 
                        color="error" 
                        onClick={() => removeEmployee(index)}
                        startIcon={<DeleteIcon />}
                        size="small"
                      >
                        Remove
                      </Button>
                    )}
                  </Box>

                  {/* Employee Project Assignment Section - FIXED */}
                  {selectedCompany && selectedCompany.id !== 'direct' && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" component="h3" fontWeight="medium" mb={2} color="text.secondary">
                        Project Assignment
                      </Typography>
                      
                      {/* Display message if no projects available */}
                      {combinedProjects.length === 0 && (
                        <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                          No projects available for this company. Please add projects to the company first.
                        </Typography>
                      )}
                      
                      {combinedProjects.length > 0 && (
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <FormControl fullWidth required>
                              <InputLabel id={`project-select-label-${index}`}>Assigned Projects</InputLabel>
                              <Select
                                labelId={`project-select-label-${index}`}
                                id={`project-select-${index}`}
                                multiple
                                value={[
                                  ...employee.projectIds.map(id => `regular_${id}`), 
                                  ...employee.customProjectIds.map(id => `custom_${id}`)
                                ]}
                                onChange={(e) => handleEmployeeProjectChange(index, e)}
                                input={<OutlinedInput label="Assigned Projects" />}
                                renderValue={(selected) => {
                                  // Display selected project names
                                  const projectNames = selected.map(id => {
                                    const project = combinedProjects.find(p => p.id === id);
                                    return project ? project.name : id;
                                  }).filter(Boolean);
                                  
                                  return projectNames.join(', ');
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 224
                                    },
                                  },
                                }}
                              >
                                {/* Regular projects header */}
                                {companyProjects.length > 0 && (
                                  <MenuItem disabled divider>
                                    <Typography variant="subtitle2" color="text.secondary">
                                      Regular Projects
                                    </Typography>
                                  </MenuItem>
                                )}
                                
                                {/* Regular project items */}
                                {companyProjects.map((project) => (
                                  <MenuItem key={`regular_${project.project_id}`} value={`regular_${project.project_id}`}>
                                    <Checkbox 
                                      checked={employee.projectIds.indexOf(project.project_id) > -1} 
                                    />
                                    <ListItemText primary={project.name} />
                                  </MenuItem>
                                ))}
                                
                                {/* Custom projects header */}
                                {customProjects.length > 0 && companyProjects.length > 0 && (
                                  <MenuItem disabled divider>
                                    <Typography variant="subtitle2" color="text.secondary">
                                      Custom Projects
                                    </Typography>
                                  </MenuItem>
                                )}
                                
                                {/* Custom project items */}
                                {customProjects.map((project) => (
                                  <MenuItem key={`custom_${project.id}`} value={`custom_${project.id}`}>
                                    <Checkbox 
                                      checked={employee.customProjectIds.indexOf(project.id) > -1} 
                                    />
                                    <ListItemText primary={project.name} />
                                  </MenuItem>
                                ))}
                              </Select>
                              <Typography variant="caption" color="text.secondary">
                                Select projects this employee will work on
                              </Typography>
                            </FormControl>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  )}

                  {/* Personal Information Section */}
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h3" fontWeight="medium" mb={2} color="text.secondary">
                      Personal Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                       <TextField
  fullWidth
  required
  id={`fullName-${index}`}
  name="fullName"
  label="Full Name"
  value={employee.fullName}
  onChange={(e) => handleEmployeeChange(index, e)}
  variant="outlined"
  error={
    !employee.fullName.trim() ||
    !/^[a-zA-Z\s.'-]+$/.test(employee.fullName.trim())
  }
  helperText={
    !employee.fullName.trim()
      ? "Full Name is required"
      : !/^[a-zA-Z\s.'-]+$/.test(employee.fullName.trim())
      ? "Only letters, spaces, apostrophes, periods, and hyphens are allowed"
      : ""
  }
/>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          id={`email-${index}`}
                          name="email"
                          label="Personal Email"
                          type="email"
                          value={employee.email}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          onBlur={(e) => checkPersonalEmailUniqueness(index, e.target.value, employee.companyEmail)}
                          variant="outlined"
                          error={!!validationErrors[`${index}-email`]}
                          helperText={validationErrors[`${index}-email`] || 'Enter contractor personal email address'}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required={companyInfo.emailGenMode === 'manual'}
                          id={`companyEmail-${index}`}
                          name="companyEmail"
                          label="Company Email"
                          type="email"
                          value={employee.companyEmail || ''}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          onBlur={(e) => checkCompanyEmailUniqueness(index, e.target.value, employee.email)}
                          variant="outlined"
                          error={!!validationErrors[`${index}-companyEmail`]}
                          helperText={
                            validationErrors[`${index}-companyEmail`] || (companyInfo.emailGenMode === 'automated'
                              ? "Suggestions will appear as you type name. You can click a suggestion or edit manually."
                              : "Enter official company email address.")
                          }
                        />
                        {companyInfo.emailGenMode === 'automated' && getEmailSuggestions(employee.fullName).length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>Suggestions:</Typography>
                            {getEmailSuggestions(employee.fullName).map((suggestion) => (
                              <Chip
                                key={suggestion}
                                label={suggestion}
                                clickable
                                color={employee.companyEmail === suggestion ? "primary" : "default"}
                                onClick={() => {
                                  const updated = [...employees];
                                  updated[index] = { ...updated[index], companyEmail: suggestion };
                                  setEmployees(updated);
                                  checkCompanyEmailUniqueness(index, suggestion, employee.email);
                                }}
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          id={`phoneNumber-${index}`}
                          name="phoneNumber"
                          type="number"
                          label="Phone Number"
                          value={employee.phoneNumber}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          variant="outlined"
                          error={!!validationErrors[`${index}-phoneNumber`]}
  helperText={validationErrors[`${index}-phoneNumber`] || 'Enter 10-digit Indian mobile number'}
  InputProps={{
    // endAdornment: employee.phoneNumber && (
    //   <InputAdornment position="end">
    //     {validationErrors[`${index}-phoneNumber`] ? 
    //       <ErrorIcon color="error" /> : 
    //       validatePhoneNumber(employee.phoneNumber) ? <CheckCircleIcon color="success" /> : null
    //     }
    //   </InputAdornment>
    // )
    onKeyDown: (e) => {
      // Prevent entering negative sign and 'e'
      if (e.key === '-' || e.key === 'e' || e.key === 'E') {
        e.preventDefault();
      }
    }
  }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel id={`gender-select-label-${index}`}>Gender</InputLabel>
                          <Select
                            labelId={`gender-select-label-${index}`}
                            id={`gender-select-${index}`}
                            name="gender"
                            value={employee.gender}
                            label="Gender"
                            onChange={(e) => handleEmployeeChange(index, e)}
                          >
                            <MenuItem value="">
                              <em>Select Gender</em>
                            </MenuItem>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          id={`aadhaarNumber-${index}`}
                          name="aadhaarNumber"
                          label={companyCountry.toLowerCase() === 'india' ? 'Aadhar Number' : 'Secondary ID'}
                          type="number"
                          value={employee.aadhaarNumber}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          variant="outlined"
                          error={!!validationErrors[`${index}-aadhaarNumber`]}
  helperText={validationErrors[`${index}-aadhaarNumber`] || `Enter ${companyCountry.toLowerCase() === 'india' ? 'Aadhar Number' : 'Secondary ID'} (optional)`}
  InputProps={{
    // endAdornment: employee.aadhaarNumber && (
    //   <InputAdornment position="end">
    //     {validationErrors[`${index}-aadhaarNumber`] ? 
    //       <ErrorIcon color="error" /> : 
    //       validateAadharNumber(employee.aadhaarNumber) ? <CheckCircleIcon color="success" /> : null
    //     }
    //   </InputAdornment>
    // )
    onKeyDown: (e) => {
      // Prevent entering negative sign and 'e'
      if (e.key === '-' || e.key === 'e' || e.key === 'E') {
        e.preventDefault();
      }
    }
  }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          id={`panNumber-${index}`}
                          name="panNumber"
                          label={nationalIdLabel}
                          value={employee.panNumber}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          variant="outlined"
                          error={!!validationErrors[`${index}-panNumber`]}
  helperText={validationErrors[`${index}-panNumber`] || `Format: ${companyCountry.toLowerCase() === 'india' ? 'ABCDE1234F' : 'Valid ID'} (optional)`}
  // InputProps={{
  //   endAdornment: employee.panNumber && (
  //     <InputAdornment position="end">
  //       {validationErrors[`${index}-panNumber`] ? 
  //         <ErrorIcon color="error" /> : 
  //         validatePanNumber(employee.panNumber) ? <CheckCircleIcon color="success" /> : null
  //       }
  //     </InputAdornment>
  //   )
  // }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Button
                          component="label"
                          variant="outlined"
                          fullWidth
                          startIcon={<CloudUploadIcon />}
                          color={employee.files.aadharCard ? "success" : "primary"}
                          sx={{ height: '56px' }}
                        >
                          {employee.files.aadharCard ? `${companyCountry.toLowerCase() === 'india' ? 'Aadhar Card' : 'Secondary ID'} Selected` : `Upload ${companyCountry.toLowerCase() === 'india' ? 'Aadhar Card' : 'Secondary ID'}`}
                          <VisuallyHiddenInput 
                            type="file" 
                            name="aadharCard"
                            onChange={(e) => handleEmployeeChange(index, e)}
                          />
                        </Button>
                        {employee.files.aadharCard && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            File: {employee.files.aadharCard.name}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Button
                          component="label"
                          variant="outlined"
                          fullWidth
                          startIcon={<CloudUploadIcon />}
                          color={employee.files.panCard ? "success" : "primary"}
                          sx={{ height: '56px' }}
                        >
                          {employee.files.panCard ? `${nationalIdLabel} Selected` : `Upload ${nationalIdLabel}`}
                          <VisuallyHiddenInput 
                            type="file" 
                            name="panCard"
                            onChange={(e) => handleEmployeeChange(index, e)}
                          />
                        </Button>
                        {employee.files.panCard && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            File: {employee.files.panCard.name}
                          </Typography>
                        )}
                      </Grid>
                      {selectedCompany && selectedCompany.id === 'direct' && (
                        <Grid item xs={12} md={6}>
                          <Box>
                            <Button
                              component="label"
                              variant="outlined"
                              fullWidth
                              startIcon={<CloudUploadIcon />}
                              color={employee.files.ndaDoc ? "success" : "primary"}
                              sx={{ height: '56px' }}
                            >
                              {employee.files.ndaDoc ? "NDA Document Selected" : "Upload NDA Document (Optional)"}
                              <VisuallyHiddenInput 
                                type="file" 
                                name="ndaDoc"
                                onChange={(e) => handleEmployeeChange(index, e)}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              />
                            </Button>
                            {employee.files.ndaDoc ? (
                              <Typography variant="caption" color="success.main" sx={{ mt: 1, pl: 1, display: 'block', fontWeight: 'medium' }}>
                                Selected File: {employee.files.ndaDoc.name}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.8, pl: 1, display: 'block', fontStyle: 'italic' }}>
                                * If the NDA is signed, please upload the document here.
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      )}

                    </Grid>
                  </Box>

                  {/* Direct Billing & Bank Remittance Details - Only visible for Direct Contractors */}
                  {selectedCompany && selectedCompany.id === 'direct' && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" component="h3" fontWeight="medium" mb={2} color="text.secondary">
                          Direct Billing & Bank Remittance Details
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              required
                              id={`hourlyRate-${index}`}
                              name="hourlyRate"
                              label={`Hourly Rate (${currencySymbol})`}
                              type="number"
                              value={employee.hourlyRate || ''}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                              InputProps={{
                                startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                                onKeyDown: (e) => {
                                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                    e.preventDefault();
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              required
                              id={`approvedHours-${index}`}
                              name="approvedHours"
                              label="Monthly Approved Hours"
                              type="number"
                              value={employee.approvedHours || '160'}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                              helperText="Timesheet-approved hours for this contractor (default: 160)"
                              InputProps={{
                                onKeyDown: (e) => {
                                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                    e.preventDefault();
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              required
                              id={`contractPeriod-${index}`}
                              name="contractPeriod"
                              label="Contract Period (e.g., 6 Months)"
                              value={employee.contractPeriod || ''}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel id={`costCenter-label-${index}`}>Cost Center (Optional)</InputLabel>
                              <Select
                                labelId={`costCenter-label-${index}`}
                                id={`costCenter-select-${index}`}
                                name="costCenterId"
                                value={employee.costCenterId || ''}
                                label="Cost Center (Optional)"
                                onChange={(e) => handleEmployeeChange(index, e)}
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {costCenters.map((cc) => (
                                  <MenuItem key={cc.id} value={cc.id}>
                                    {cc.name} ({cc.code})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              required
                              id={`contractStartDate-${index}`}
                              name="contractStartDate"
                              label="Contract Start Date"
                              type="date"
                              value={employee.contractStartDate || ''}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              required
                              id={`contractEndDate-${index}`}
                              name="contractEndDate"
                              label="Contract End Date"
                              type="date"
                              value={employee.contractEndDate || ''}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              required
                              id={`bankName-${index}`}
                              name="bankName"
                              label="Bank Name"
                              value={employee.bankName || ''}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              required
                              id={`accountNumber-${index}`}
                              name="accountNumber"
                              label="Account Number"
                              value={employee.accountNumber || ''}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              required
                              id={`ifscCode-${index}`}
                              name="ifscCode"
                              label={bankCodeLabel}
                              value={employee.ifscCode || ''}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              required
                              id={`accountName-${index}`}
                              name="accountName"
                              label="Account Holder Name"
                              value={employee.accountName || ''}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              required
                              id={`address-${index}`}
                              name="address"
                              label="Direct Contractor Address"
                              value={employee.address || ''}
                              onChange={(e) => handleEmployeeChange(index, e)}
                              variant="outlined"
                              multiline
                              rows={2}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </>
                  )}

                  {/* Role Information Section */}
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" component="h3" fontWeight="medium" mb={2} color="text.secondary">
                      Role Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          id={`designation-${index}`}
                          name="designation"
                          label="Designation"
                          value={employee.designation}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          id={`department-${index}`}
                          name="department"
                          label="Department"
                          value={employee.department}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel id={`roleType-select-label-${index}`}>Role Type</InputLabel>
                          <Select
                            labelId={`roleType-select-label-${index}`}
                            id={`roleType-select-${index}`}
                            name="roleType"
                            value={employee.roleType}
                            label="Role Type"
                            onChange={(e) => handleEmployeeChange(index, e)}
                          >
                            <MenuItem value="">
                              <em>Select Role Type</em>
                            </MenuItem>
                            {roleTypes.map((role) => (
                              <MenuItem key={role} value={role}>
                                {role}
                              </MenuItem>
                            ))}
                            <MenuItem value="Other">Other (Custom Role Type)</MenuItem>
                          </Select>
                        </FormControl>
                        {employee.roleType === 'Other' && (
                          <TextField
                            fullWidth
                            required
                            id={`customRoleType-${index}`}
                            name="customRoleType"
                            label="Custom Role Type"
                            value={employee.customRoleType || ''}
                            onChange={(e) => handleEmployeeChange(index, e)}
                            variant="outlined"
                            sx={{ mt: 2 }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          id={`reportingManager-${index}`}
                          name="reportingManager"
                          label="Reporting Manager"
                          value={employee.reportingManager}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          variant="outlined"
                        />
                      </Grid>
                      {/* Added Skills field */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          id={`skills-${index}`}
                          name="skills"
                          label="Key Skills"
                          placeholder="E.g. Java, React, Python, Project Management"
                          value={employee.skills}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          variant="outlined"
                        />
                      </Grid>
                      {/* Added Skills Description field */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          id={`skillsDescription-${index}`}
                          name="skillsDescription"
                          label="Skills Description"
                          placeholder="Detailed description of skills, experience, and expertise"
                          value={employee.skillsDescription}
                          onChange={(e) => handleEmployeeChange(index, e)}
                          variant="outlined"
                          multiline
                          rows={4}
                          helperText="Describe the employee's key skills, expertise levels, certifications, and relevant experience"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            ))}
            
            {/* Add Employee Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={addEmployee}
                startIcon={<AddCircleIcon />}
                size="large"
              >
                Add Another Employee
              </Button>
            </Box>
            
            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                startIcon={<PersonAddIcon />}
                sx={{ minWidth: 200, py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : 'Submit Employees'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
}